import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import GestorLayout from '../../components/GestorLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  AlertTriangle, Bell, Clock, CheckCircle2, RefreshCw,
  User, Package, Layers, TrendingDown, TrendingUp, Filter,
  Settings, Play, Shield, PhoneCall, Plus, Pencil, Trash2, Eye, Copy,
  Send, CalendarClock,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GestaoAlertas() {
  const [loading, setLoading] = useState(true);
  const [alertasAnomalia, setAlertasAnomalia] = useState([]);
  const [alertasInatividade, setAlertasInatividade] = useState([]);
  const [alertasContagem, setAlertasContagem] = useState([]);
  const [alertasChamarGestor, setAlertasChamarGestor] = useState([]);
  const [filtroResolvido, setFiltroResolvido] = useState(false);
  const [activeTab, setActiveTab] = useState('todos');
  // Timeout config
  const [maxSessionHoras, setMaxSessionHoras] = useState(24);
  const [savingTimeout, setSavingTimeout] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  // Meus Alertas (do gestor)
  const [meusAlertas, setMeusAlertas] = useState([]);
  const [showFormAlerta, setShowFormAlerta] = useState(false);
  const [editandoAlerta, setEditandoAlerta] = useState(null);
  const [novoAlerta, setNovoAlerta] = useState({ titulo: '', mensagem: '', data_notificacao: '', destinatarios: 'todos', prioridade: 'normal' });
  const [saving, setSaving] = useState(false);
  // Preferências de alertas
  const [prefsAlertas, setPrefsAlertas] = useState({ tipos_disponiveis: [], tipos_ativos: [] });
  const [showPrefsDialog, setShowPrefsDialog] = useState(false);
  const [operadoresList, setOperadoresList] = useState([]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [anomaliaRes, inatividadeRes, contagemRes, timeoutRes, chamadasRes, alertasRes, prefsRes] = await Promise.all([
        axios.get(`${API}/alertas-anomalias`),
        axios.get(`${API}/alertas-inatividade`),
        axios.get(`${API}/alertas-contagem`),
        axios.get(`${API}/producao/timeout/config`),
        axios.get(`${API}/alertas-gestor/chamadas`).catch(() => ({ data: [] })),
        axios.get(`${API}/configuracoes/alertas`).catch(() => ({ data: [] })),
        axios.get(`${API}/preferencias-alertas`).catch(() => ({ data: { tipos_disponiveis: [], tipos_ativos: ['chamadas', 'anomalia', 'inatividade', 'contagem'] } })),
      ]);
      setAlertasAnomalia(anomaliaRes.data || []);
      setAlertasInatividade(inatividadeRes.data || []);
      setAlertasContagem(contagemRes.data || []);
      setMaxSessionHoras(timeoutRes.data?.max_session_horas || 24);
      setAlertasChamarGestor(chamadasRes.data || []);
      setMeusAlertas(alertasRes.data || []);
      setPrefsAlertas(prefsRes.data || { tipos_disponiveis: [], tipos_ativos: [] });
      
      // Carregar lista de operadores para seleção de destinatários
      try {
        const opsRes = await axios.get(`${API}/operadores`);
        setOperadoresList((opsRes.data || []).filter(o => o.perfil === 'operador'));
      } catch { /* silent */ }
    } catch (e) {
      toast.error('Erro ao carregar alertas');
    } finally {
      setLoading(false);
    }
  };

  // SSE (Server-Sent Events) para alertas em tempo real + polling fallback
  const prevChamadasRef = useRef(-1); // -1 = primeira carga (não notificar)
  useEffect(() => {
    loadData();

    // Tentar SSE primeiro
    let eventSource = null;
    let fallbackInterval = null;
    try {
      eventSource = new EventSource(`${API}/alertas/stream`);
      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          // Só notificar se houve NOVAS chamadas RECENTES (não antigas ou na primeira carga)
          if (prevChamadasRef.current >= 0 && data.chamadas > prevChamadasRef.current) {
            // SSE apenas notifica contagem — verificar se há chamada recente antes de toastar
            // (evita popup para chamadas antigas quando contagem muda por outros motivos)
          }
          prevChamadasRef.current = data.chamadas || 0;
          // Recarregar dados silenciosamente
          Promise.all([
            axios.get(`${API}/alertas-gestor/chamadas`).catch(() => ({ data: [] })),
            axios.get(`${API}/alertas-anomalias`).catch(() => ({ data: [] })),
            axios.get(`${API}/alertas-contagem`).catch(() => ({ data: [] })),
          ]).then(([chamadasRes, anomaliaRes, contagemRes]) => {
            setAlertasChamarGestor(chamadasRes.data || []);
            setAlertasAnomalia(anomaliaRes.data || []);
            setAlertasContagem(contagemRes.data || []);
          });
        } catch { /* parse error */ }
      };
      eventSource.onerror = () => {
        // Fallback para polling se SSE falhar
        eventSource.close();
        eventSource = null;
        startPolling();
      };
    } catch {
      startPolling();
    }

    function startPolling() {
      if (fallbackInterval) return;
      fallbackInterval = setInterval(async () => {
        try {
          const [chamadasRes, anomaliaRes, contagemRes] = await Promise.all([
            axios.get(`${API}/alertas-gestor/chamadas`).catch(() => ({ data: [] })),
            axios.get(`${API}/alertas-anomalias`).catch(() => ({ data: [] })),
            axios.get(`${API}/alertas-contagem`).catch(() => ({ data: [] })),
          ]);
          const novasChamadas = chamadasRes.data || [];
          const naoResolvidas = novasChamadas.filter(a => !a.resolvido).length;
          setAlertasChamarGestor(novasChamadas);
          setAlertasAnomalia(anomaliaRes.data || []);
          setAlertasContagem(contagemRes.data || []);
          if (prevChamadasRef.current >= 0 && naoResolvidas > prevChamadasRef.current) {
            // Verificar se a chamada mais recente é de facto recente (< 60s)
            const maisRecente = novasChamadas.find(a => !a.resolvido);
            const eRecente = maisRecente?.criado_em && (Date.now() - new Date(maisRecente.criado_em).getTime()) < 60000;
            if (eRecente) {
              toast.error('Novo pedido de ajuda de um operador!', { duration: 8000 });
            }
          }
          prevChamadasRef.current = naoResolvidas;
        } catch { /* silently fail */ }
      }, 10000);
    }

    return () => {
      if (eventSource) eventSource.close();
      if (fallbackInterval) clearInterval(fallbackInterval);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const resolverAlertaAnomalia = async (id) => {
    try {
      await axios.put(`${API}/alertas-anomalias/${id}/resolver`, { resolvido_por: 'gestor' });
      setAlertasAnomalia(prev => prev.map(a => a.id === id ? { ...a, resolvido: true } : a));
      toast.success('Alerta resolvido');
    } catch (e) {
      toast.error('Erro ao resolver alerta');
    }
  };

  const resolverAlertaInatividade = async (id) => {
    try {
      await axios.put(`${API}/alertas-inatividade/${id}/resolver`, { resolvido_por: 'gestor' });
      setAlertasInatividade(prev => prev.map(a => a.id === id ? { ...a, resolvido: true } : a));
      toast.success('Alerta resolvido');
    } catch (e) {
      toast.error('Erro ao resolver alerta');
    }
  };

  const resolverAlertaContagem = async (id) => {
    try {
      await axios.put(`${API}/alertas-contagem/${id}/resolver`);
      setAlertasContagem(prev => prev.map(a => a.id === id ? { ...a, resolvido: true } : a));
      toast.success('Alerta resolvido');
    } catch (e) {
      toast.error('Erro ao resolver alerta');
    }
  };

  const resolverChamadaGestor = async (id) => {
    try {
      await axios.put(`${API}/alertas-gestor/chamadas/${id}/resolver`);
      setAlertasChamarGestor(prev => prev.map(a => a.id === id ? { ...a, resolvido: true } : a));
      toast.success('Pedido resolvido');
    } catch (e) {
      toast.error('Erro ao resolver pedido');
    }
  };

  // CRUD — Meus Alertas (do gestor)
  const salvarMeuAlerta = async () => {
    if (!novoAlerta.titulo || !novoAlerta.mensagem) return;
    setSaving(true);
    try {
      // Se destinatários é um UUID (operador individual), ajustar payload
      const payload = { ...novoAlerta };
      const isOperadorId = !['todos', 'gestores', 'operadores'].includes(payload.destinatarios);
      if (isOperadorId) {
        payload.destinatario_especifico = payload.destinatarios;
        payload.destinatarios = 'operadores';
      }
      
      if (editandoAlerta) {
        await axios.put(`${API}/configuracoes/alertas/${editandoAlerta.id}`, payload);
        toast.success('Alerta atualizado');
      } else {
        await axios.post(`${API}/configuracoes/alertas`, payload);
        toast.success('Alerta criado');
      }
      setShowFormAlerta(false);
      setEditandoAlerta(null);
      loadData();
    } catch {
      toast.error('Erro ao guardar alerta');
    } finally {
      setSaving(false);
    }
  };

  const eliminarMeuAlerta = async (id) => {
    if (!window.confirm('Tem a certeza que deseja eliminar este alerta?')) return;
    try {
      await axios.delete(`${API}/configuracoes/alertas/${id}`);
      setMeusAlertas(prev => prev.filter(a => a.id !== id));
      toast.success('Alerta eliminado');
    } catch {
      toast.error('Erro ao eliminar alerta');
    }
  };

  const filtrar = (lista) => filtroResolvido ? lista : lista.filter(a => !a.resolvido);

  const anomaliasFiltradas = filtrar(alertasAnomalia).sort((a, b) => new Date(b.data_hora || b.criado_em || 0) - new Date(a.data_hora || a.criado_em || 0));
  const inatividadesFiltradas = filtrar(alertasInatividade).sort((a, b) => new Date(b.data_hora || b.criado_em || 0) - new Date(a.data_hora || a.criado_em || 0));
  const contagemFiltradas = filtrar(alertasContagem).filter(a => a.diferenca !== 0).sort((a, b) => new Date(b.data || b.criado_em || 0) - new Date(a.data || a.criado_em || 0));
  const chamadasFiltradas = filtrar(alertasChamarGestor).sort((a, b) => new Date(b.criado_em || 0) - new Date(a.criado_em || 0));

  const chamadasNaoResolvidas = alertasChamarGestor.filter(a => !a.resolvido).length;
  const tiposAtivos = prefsAlertas.tipos_ativos || [];
  const totalNaoResolvidos = 
    (tiposAtivos.includes('anomalia') ? alertasAnomalia.filter(a => !a.resolvido).length : 0) +
    (tiposAtivos.includes('inatividade') ? alertasInatividade.filter(a => !a.resolvido).length : 0) +
    (tiposAtivos.includes('contagem') ? alertasContagem.filter(a => !a.resolvido && a.diferenca !== 0).length : 0) +
    (tiposAtivos.includes('chamadas') ? chamadasNaoResolvidas : 0);

  // Merged list for "Todos" tab — filtrada pelas preferências de alertas
  const todosAlertas = (() => {
    const all = [];
    if (tiposAtivos.includes('chamadas')) chamadasFiltradas.forEach(a => all.push({ ...a, _tipo: 'chamada', _date: a.criado_em }));
    if (tiposAtivos.includes('anomalia')) anomaliasFiltradas.forEach(a => all.push({ ...a, _tipo: 'anomalia', _date: a.data_hora || a.criado_em }));
    if (tiposAtivos.includes('inatividade')) inatividadesFiltradas.forEach(a => all.push({ ...a, _tipo: 'inatividade', _date: a.data_hora || a.criado_em }));
    if (tiposAtivos.includes('contagem')) contagemFiltradas.forEach(a => all.push({ ...a, _tipo: 'contagem', _date: a.data || a.criado_em }));
    all.sort((a, b) => new Date(b._date || 0) - new Date(a._date || 0));
    return all;
  })();

  const tabs = [
    { id: 'todos', label: 'Todos', count: totalNaoResolvidos, icon: Layers, color: 'text-navy' },
    { id: 'chamadas', label: 'Pedidos de Ajuda', count: chamadasNaoResolvidas, icon: PhoneCall, color: 'text-red-600' },
    { id: 'anomalia', label: 'Anomalias', count: alertasAnomalia.filter(a => !a.resolvido).length, icon: TrendingDown, color: 'text-amber-600' },
    { id: 'inatividade', label: 'Inatividade', count: alertasInatividade.filter(a => !a.resolvido).length, icon: Clock, color: 'text-blue-600' },
    { id: 'contagem', label: 'Contagem', count: alertasContagem.filter(a => !a.resolvido && a.diferenca !== 0).length, icon: TrendingUp, color: 'text-purple-600' },
    { id: 'meus', label: 'Meus Alertas', count: meusAlertas.length, icon: Send, color: 'text-indigo-600', highlight: true },
  ];

  const toggleTipoAlerta = async (tipoId) => {
    const novos = tiposAtivos.includes(tipoId)
      ? tiposAtivos.filter(t => t !== tipoId)
      : [...tiposAtivos, tipoId];
    try {
      await axios.put(`${API}/preferencias-alertas`, { tipos_ativos: novos });
      setPrefsAlertas(prev => ({ ...prev, tipos_ativos: novos }));
      toast.success('Preferencias atualizadas');
    } catch {
      toast.error('Erro ao guardar preferencias');
    }
  };

  const ICON_MAP = { chamadas: PhoneCall, anomalia: TrendingDown, inatividade: Clock, contagem: TrendingUp };
  const COLOR_MAP = { chamadas: '#EF4444', anomalia: '#F59E0B', inatividade: '#3B82F6', contagem: '#8B5CF6' };

  const formatDateTime = (dt) => dt ? new Date(dt).toLocaleString('pt-PT', { dateStyle: 'short', timeStyle: 'short' }) : '—';

  return (
    <>
    <GestorLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading text-navy flex items-center gap-2">
              <Bell className="h-6 w-6 text-amber-500" />
              Gestão de Alertas
            </h1>
            <p className="text-stone-500 text-sm mt-1">
              {totalNaoResolvidos > 0
                ? `${totalNaoResolvidos} alerta(s) por resolver`
                : 'Sem alertas pendentes'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowPrefsDialog(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-stone-200 hover:bg-stone-50 text-sm text-stone-600 transition-colors"
              data-testid="btn-prefs-alertas"
            >
              <Settings className="h-4 w-4" />
              Notificacoes
            </button>
            <label className="flex items-center gap-2 text-sm text-stone-600 cursor-pointer px-3 py-2 rounded-lg border border-stone-200 hover:bg-stone-50">
              <input
                type="checkbox"
                checked={filtroResolvido}
                onChange={(e) => setFiltroResolvido(e.target.checked)}
                className="rounded"
              />
              <Filter className="h-3.5 w-3.5" />
              Mostrar resolvidos
            </label>
            <Button variant="outline" size="sm" onClick={loadData} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Atualizar
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-stone-200">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            const isHighlight = tab.highlight;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  isActive
                    ? isHighlight ? 'border-indigo-600 text-indigo-600 bg-indigo-50/60' : 'border-primary text-primary'
                    : isHighlight ? 'border-transparent text-indigo-500 hover:text-indigo-700 hover:bg-indigo-50/40' : 'border-transparent text-stone-500 hover:text-stone-700'
                }`}
                data-testid={`tab-alertas-${tab.id}`}
              >
                <Icon className={`h-4 w-4 ${isActive && isHighlight ? 'text-indigo-600' : tab.count > 0 ? tab.color : 'text-stone-400'}`} />
                <span className={isHighlight ? 'font-bold' : ''}>{tab.label}</span>
                {tab.count > 0 && (
                  <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs font-bold ${isHighlight ? 'bg-indigo-100 text-indigo-700' : 'bg-red-100 text-red-700'}`}>
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <RefreshCw className="h-8 w-8 text-stone-400 animate-spin" />
          </div>
        ) : (
          <>
            {/* Tab: Todos (Vista unificada) */}
            {activeTab === 'todos' && (
              <div className="space-y-3">
                {todosAlertas.length === 0 ? (
                  <Card className="p-8 text-center">
                    <CheckCircle2 className="h-12 w-12 text-green-400 mx-auto mb-3" />
                    <p className="text-stone-600 font-medium">Sem alertas{!filtroResolvido ? ' pendentes' : ''}</p>
                    <p className="text-sm text-stone-400">Todos os alertas aparecem aqui</p>
                  </Card>
                ) : (
                  todosAlertas.map(alerta => {
                    const tipo = alerta._tipo;
                    const tipoConfig = {
                      chamada: { icon: PhoneCall, color: 'text-red-600', bg: 'bg-red-100', border: 'border-l-red-500', label: 'Pedido de Ajuda' },
                      anomalia: { icon: TrendingDown, color: 'text-amber-600', bg: 'bg-amber-100', border: 'border-l-amber-400', label: 'Anomalia' },
                      inatividade: { icon: Clock, color: 'text-blue-600', bg: 'bg-blue-100', border: 'border-l-blue-400', label: 'Inatividade' },
                      contagem: { icon: alerta.diferenca < 0 ? TrendingDown : TrendingUp, color: alerta.diferenca < 0 ? 'text-red-600' : 'text-emerald-600', bg: alerta.diferenca < 0 ? 'bg-red-100' : 'bg-emerald-100', border: alerta.diferenca < 0 ? 'border-l-red-400' : 'border-l-emerald-400', label: 'Contagem' },
                    }[tipo];
                    const Icon = tipoConfig.icon;
                    return (
                      <Card key={`${tipo}-${alerta.id}`} className={`p-4 ${alerta.resolvido ? 'opacity-60 bg-stone-50' : `border-l-4 ${tipoConfig.border}`}`} data-testid={`alerta-todos-${alerta.id}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 flex-1">
                            <div className={`w-9 h-9 rounded-full ${tipoConfig.bg} flex items-center justify-center flex-shrink-0`}>
                              <Icon className={`h-4 w-4 ${tipoConfig.color}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap mb-1">
                                <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${tipoConfig.bg} ${tipoConfig.color}`}>{tipoConfig.label}</span>
                                {tipo === 'chamada' && <span className="font-bold text-red-700">{alerta.operador_nome}</span>}
                                {tipo === 'anomalia' && <span className="font-semibold text-navy">{alerta.operador_nome}</span>}
                                {tipo === 'inatividade' && <span className="font-semibold text-navy">{alerta.operador_nome}</span>}
                                {tipo === 'contagem' && alerta.operador_nome && <span className="font-semibold text-navy">{alerta.operador_nome}</span>}
                                {alerta.of_numero && <span className="text-sm font-bold text-blue-700">OF {alerta.of_numero}</span>}
                                {alerta.lote_codigo && <span className="text-xs px-1.5 py-0.5 bg-stone-100 text-stone-600 rounded">{alerta.lote_codigo}</span>}
                              </div>
                              {/* Marca / Modelo - visível em todos os tipos */}
                              {(alerta.marca || alerta.modelo) && (
                                <p className="text-sm text-stone-600 mb-1">
                                  {alerta.marca && <span className="font-medium">{alerta.marca}</span>}
                                  {alerta.marca && alerta.modelo ? ' · ' : ''}
                                  {alerta.modelo && <span>{alerta.modelo}</span>}
                                </p>
                              )}
                              {tipo === 'contagem' && (
                                <>
                                  {/* Desvio compacto */}
                                  <div className="flex items-center gap-3 mt-1" data-testid={`desvio-visual-${alerta.id}`}>
                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md font-black text-sm ${
                                      alerta.diferenca < 0 
                                        ? 'bg-red-100 text-red-700' 
                                        : 'bg-emerald-100 text-emerald-700'
                                    }`}>
                                      {alerta.diferenca < 0 ? <TrendingDown className="h-3.5 w-3.5" /> : <TrendingUp className="h-3.5 w-3.5" />}
                                      {alerta.diferenca > 0 ? '+' : ''}{alerta.diferenca}
                                    </span>
                                    <span className="text-xs text-stone-500">Esp: <strong>{alerta.quantidade_esperada}</strong></span>
                                    <span className="text-xs text-stone-500">Cont: <strong className={alerta.diferenca < 0 ? 'text-red-600' : 'text-emerald-600'}>{alerta.quantidade_contada}</strong></span>
                                  </div>
                                </>
                              )}
                              {tipo === 'anomalia' && (
                                <div className="flex items-center gap-4 text-sm">
                                  {alerta.descricao ? (
                                    <span className="text-amber-600 font-medium">{alerta.quantidade_anomalias} anomalia{alerta.quantidade_anomalias !== 1 ? 's' : ''} registada{alerta.quantidade_anomalias !== 1 ? 's' : ''}</span>
                                  ) : (
                                    <>
                                      <span className="text-stone-500">Solicitado: <strong>{alerta.quantidade_solicitada}</strong></span>
                                      <span className="text-amber-600 font-medium">+{alerta.quantidade_anomalias} anomalias</span>
                                    </>
                                  )}
                                </div>
                              )}
                              {(alerta.operacao_nome || alerta.operacao_codigo) && (
                                <p className="text-sm text-stone-700">
                                  {alerta.operacao_codigo && <span className="font-mono mr-1">{alerta.operacao_codigo}</span>}
                                  {alerta.operacao_nome && <strong>{alerta.operacao_nome}</strong>}
                                </p>
                              )}
                              {tipo === 'contagem' && alerta.operador_nome && (
                                <p className="text-sm text-stone-500"><User className="inline h-3.5 w-3.5 mr-1" />{alerta.operador_nome}</p>
                              )}
                              <p className="text-xs text-stone-400 mt-1">{formatDateTime(alerta._date)}</p>
                            </div>
                          </div>
                          {!alerta.resolvido ? (
                            <Button size="sm" variant="outline"
                              className="text-red-700 border-red-200 hover:bg-red-50"
                              onClick={() => {
                                if (tipo === 'chamada') resolverChamadaGestor(alerta.id);
                                else if (tipo === 'anomalia') resolverAlertaAnomalia(alerta.id);
                                else if (tipo === 'inatividade') resolverAlertaInatividade(alerta.id);
                                else if (tipo === 'contagem') resolverAlertaContagem(alerta.id);
                              }}
                              data-testid={`resolver-todos-${alerta.id}`}
                            >
                              <CheckCircle2 className="h-4 w-4 mr-1" /> Resolvido
                            </Button>
                          ) : (
                            <span className="text-xs text-green-600 flex items-center gap-1 flex-shrink-0">
                              <CheckCircle2 className="h-3.5 w-3.5" /> Resolvido
                            </span>
                          )}
                        </div>
                      </Card>
                    );
                  })
                )}
              </div>
            )}

            {/* Tab: Pedidos de Ajuda (Chamar Gestor) */}
            {activeTab === 'chamadas' && (
              <div className="space-y-3">
                {chamadasFiltradas.length === 0 ? (
                  <Card className="p-8 text-center">
                    <CheckCircle2 className="h-12 w-12 text-green-400 mx-auto mb-3" />
                    <p className="text-stone-600 font-medium">Sem pedidos de ajuda{!filtroResolvido ? ' pendentes' : ''}</p>
                    <p className="text-sm text-stone-400">Estes alertas são criados quando um operador pressiona o botão "Chamar Gestor"</p>
                  </Card>
                ) : (
                  chamadasFiltradas.map(alerta => (
                    <Card key={alerta.id} className={`p-4 ${alerta.resolvido ? 'opacity-60 bg-stone-50' : 'border-l-4 border-l-red-500 bg-red-50/30'}`} data-testid={`alerta-chamada-${alerta.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1">
                          <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 animate-pulse">
                            <PhoneCall className="h-5 w-5 text-red-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <span className="font-bold text-red-700 text-base">{alerta.operador_nome}</span>
                              <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-bold rounded-full uppercase">Urgente</span>
                            </div>
                            <p className="text-sm text-stone-700">
                              {alerta.operacao_codigo && <span className="font-mono mr-1">{alerta.operacao_codigo}</span>}
                              <strong>{alerta.operacao_nome || '—'}</strong>
                            </p>
                            <div className="flex items-center gap-3 mt-1 text-sm text-stone-500">
                              {alerta.of_numero && <span>OF: <strong className="text-navy">{alerta.of_numero}</strong></span>}
                              {alerta.marca && <span>Marca: <strong>{alerta.marca}</strong></span>}
                              {alerta.modelo && <span>Modelo: <strong>{alerta.modelo}</strong></span>}
                              {alerta.lote_codigo && <span>Lote: <strong>{alerta.lote_codigo}</strong></span>}
                            </div>
                            <p className="text-xs text-stone-400 mt-1">{formatDateTime(alerta.criado_em)}</p>
                          </div>
                        </div>
                        {!alerta.resolvido ? (
                          <Button size="sm" variant="outline" className="text-red-700 border-red-200 hover:bg-red-50 flex-shrink-0"
                            onClick={() => resolverChamadaGestor(alerta.id)}
                            data-testid={`resolver-chamada-${alerta.id}`}
                          >
                            <CheckCircle2 className="h-4 w-4 mr-1" /> Resolvido
                          </Button>
                        ) : (
                          <span className="text-xs text-green-600 flex items-center gap-1 flex-shrink-0">
                            <CheckCircle2 className="h-3.5 w-3.5" /> Resolvido
                          </span>
                        )}
                      </div>
                    </Card>
                  ))
                )}
              </div>
            )}

            {/* Tab: Anomalias de Quantidade */}
            {activeTab === 'anomalia' && (
              <div className="space-y-3">
                {anomaliasFiltradas.length === 0 ? (
                  <Card className="p-8 text-center">
                    <CheckCircle2 className="h-12 w-12 text-green-400 mx-auto mb-3" />
                    <p className="text-stone-600 font-medium">Sem alertas de anomalias{!filtroResolvido ? ' por resolver' : ''}</p>
                    <p className="text-sm text-stone-400">Estes alertas são gerados quando um operador usa peças com anomalias</p>
                  </Card>
                ) : (
                  anomaliasFiltradas.map(alerta => (
                    <Card key={alerta.id} className={`p-4 ${alerta.resolvido ? 'opacity-60 bg-stone-50' : 'border-l-4 border-l-amber-400'}`} data-testid={`alerta-anomalia-${alerta.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1">
                          <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                            <TrendingDown className="h-4 w-4 text-amber-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <span className="font-semibold text-navy">{alerta.operador_nome}</span>
                              <span className="text-xs text-stone-500">•</span>
                              <span className="text-sm font-bold text-blue-700">OF {alerta.of_numero}</span>
                              {alerta.lote_codigo && (
                                <span className="text-xs px-1.5 py-0.5 bg-stone-100 text-stone-600 rounded">{alerta.lote_codigo}</span>
                              )}
                              {alerta.quantidade_lote > 0 && (
                                <span className="text-xs text-stone-500">Qtd Lote: <strong>{alerta.quantidade_lote}</strong></span>
                              )}
                            </div>
                            {(alerta.marca || alerta.modelo) && (
                              <p className="text-sm text-stone-600 mb-1">
                                {alerta.marca && <span className="font-medium">{alerta.marca}</span>}
                                {alerta.marca && alerta.modelo ? ' · ' : ''}
                                {alerta.modelo && <span>{alerta.modelo}</span>}
                              </p>
                            )}
                            <p className="text-sm text-stone-700">
                              Operação: <strong>{alerta.operacao_nome}</strong>
                            </p>
                            <div className="flex items-center gap-4 mt-1 text-sm">
                              {alerta.descricao ? (
                                <span className="text-amber-600 font-medium">{alerta.quantidade_anomalias} anomalia{alerta.quantidade_anomalias !== 1 ? 's' : ''} registada{alerta.quantidade_anomalias !== 1 ? 's' : ''}</span>
                              ) : (
                                <>
                                  <span className="text-stone-500">Solicitado: <strong className="text-navy">{alerta.quantidade_solicitada}</strong></span>
                                  <span className="text-stone-500">Disponível: <strong className="text-emerald-700">{alerta.quantidade_disponivel}</strong></span>
                                  <span className="text-amber-600 font-medium">+{alerta.quantidade_anomalias} anomalias usadas</span>
                                </>
                              )}
                            </div>
                            <p className="text-xs text-stone-400 mt-1">{formatDateTime(alerta.data_hora)}</p>
                          </div>
                        </div>
                        {!alerta.resolvido ? (
                          <Button size="sm" variant="outline" className="text-red-700 border-red-200 hover:bg-red-50 flex-shrink-0"
                            onClick={() => resolverAlertaAnomalia(alerta.id)}
                            data-testid={`resolver-anomalia-${alerta.id}`}
                          >
                            <CheckCircle2 className="h-4 w-4 mr-1" /> Resolver
                          </Button>
                        ) : (
                          <span className="text-xs text-green-600 flex items-center gap-1 flex-shrink-0">
                            <CheckCircle2 className="h-3.5 w-3.5" /> Resolvido
                          </span>
                        )}
                      </div>
                    </Card>
                  ))
                )}
              </div>
            )}

            {/* Tab: Inatividade */}
            {activeTab === 'inatividade' && (
              <div className="space-y-3">
                {inatividadesFiltradas.length === 0 ? (
                  <Card className="p-8 text-center">
                    <CheckCircle2 className="h-12 w-12 text-green-400 mx-auto mb-3" />
                    <p className="text-stone-600 font-medium">Sem alertas de inatividade{!filtroResolvido ? ' por resolver' : ''}</p>
                    <p className="text-sm text-stone-400">Estes alertas são gerados quando um operador não responde ao alerta de fim de turno</p>
                  </Card>
                ) : (
                  inatividadesFiltradas.map(alerta => (
                    <Card key={alerta.id} className={`p-4 ${alerta.resolvido ? 'opacity-60 bg-stone-50' : 'border-l-4 border-l-blue-400'}`} data-testid={`alerta-inatividade-${alerta.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1">
                          <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                            <Clock className="h-4 w-4 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <span className="font-semibold text-navy">{alerta.operador_nome}</span>
                              <span className="text-xs text-stone-500">•</span>
                              <span className="text-sm font-bold text-blue-700">OF {alerta.of_numero}</span>
                              {alerta.lote_codigo && (
                                <span className="text-xs px-1.5 py-0.5 bg-stone-100 text-stone-600 rounded">{alerta.lote_codigo}</span>
                              )}
                              {alerta.quantidade_lote > 0 && (
                                <span className="text-xs text-stone-500">Qtd Lote: <strong>{alerta.quantidade_lote}</strong></span>
                              )}
                            </div>
                            {(alerta.marca || alerta.modelo) && (
                              <p className="text-sm text-stone-600 mb-1">
                                {alerta.marca && <span className="font-medium">{alerta.marca}</span>}
                                {alerta.marca && alerta.modelo ? ' · ' : ''}
                                {alerta.modelo && <span>{alerta.modelo}</span>}
                              </p>
                            )}
                            <p className="text-sm text-stone-700">
                              Operação: <strong>{alerta.operacao_nome}</strong>
                            </p>
                            <p className="text-xs text-stone-400 mt-1">{formatDateTime(alerta.data_hora)}</p>
                            {alerta.resolvido_por && (
                              <p className="text-xs text-stone-400">Resolvido por: {alerta.resolvido_por} · {formatDateTime(alerta.resolvido_em)}</p>
                            )}
                          </div>
                        </div>
                        {!alerta.resolvido ? (
                          <Button size="sm" variant="outline" className="text-red-700 border-red-200 hover:bg-red-50 flex-shrink-0"
                            onClick={() => resolverAlertaInatividade(alerta.id)}
                            data-testid={`resolver-inatividade-${alerta.id}`}
                          >
                            <CheckCircle2 className="h-4 w-4 mr-1" /> Resolver
                          </Button>
                        ) : (
                          <span className="text-xs text-green-600 flex items-center gap-1 flex-shrink-0">
                            <CheckCircle2 className="h-3.5 w-3.5" /> Resolvido
                          </span>
                        )}
                      </div>
                    </Card>
                  ))
                )}
              </div>
            )}

            {/* Tab: Alertas de Contagem */}
            {activeTab === 'contagem' && (
              <div className="space-y-3">
                {contagemFiltradas.length === 0 ? (
                  <Card className="p-8 text-center">
                    <CheckCircle2 className="h-12 w-12 text-green-400 mx-auto mb-3" />
                    <p className="text-stone-600 font-medium">Sem alertas de contagem{!filtroResolvido ? ' por resolver' : ''}</p>
                    <p className="text-sm text-stone-400">Estes alertas são gerados quando a contagem difere da quantidade esperada</p>
                  </Card>
                ) : (
                  contagemFiltradas.map(alerta => {
                    const falta = alerta.diferenca < 0;
                    return (
                      <Card key={alerta.id} className={`overflow-hidden ${alerta.resolvido ? 'opacity-60 bg-stone-50' : ''}`} data-testid={`alerta-contagem-${alerta.id}`}>
                        <div className={`h-1 ${falta ? 'bg-red-500' : 'bg-emerald-500'}`} />
                        <div className="px-4 py-3">
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                {alerta.of_numero && <span className="text-sm font-bold text-blue-700">OF {alerta.of_numero}</span>}
                                {alerta.lote_codigo && <span className="text-xs px-1.5 py-0.5 bg-stone-100 text-stone-600 rounded font-medium">{alerta.lote_codigo}</span>}
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md font-black text-sm ${
                                  falta ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'
                                }`}>
                                  {falta ? <TrendingDown className="h-3.5 w-3.5" /> : <TrendingUp className="h-3.5 w-3.5" />}
                                  {alerta.diferenca > 0 ? '+' : ''}{alerta.diferenca}
                                </span>
                              </div>
                              {(alerta.marca || alerta.modelo) && (
                                <p className="text-sm text-stone-600 mt-1">
                                  {alerta.marca && <span className="font-medium">{alerta.marca}</span>}
                                  {alerta.marca && alerta.modelo ? ' · ' : ''}
                                  {alerta.modelo && <span>{alerta.modelo}</span>}
                                </p>
                              )}
                              <div className="flex items-center gap-2 mt-1 text-xs text-stone-500">
                                {alerta.operacao_nome && <span className="font-semibold text-stone-700">{alerta.operacao_codigo} {alerta.operacao_nome}</span>}
                                {alerta.operador_nome && <><span>·</span><span>{alerta.operador_nome}</span></>}
                              </div>
                              <div className="flex items-center gap-3 mt-1 text-xs text-stone-500">
                                <span>Esp: <strong>{alerta.quantidade_esperada}</strong></span>
                                <span>Cont: <strong className={falta ? 'text-red-600' : 'text-emerald-600'}>{alerta.quantidade_contada}</strong></span>
                                <span className="text-stone-400">{formatDateTime(alerta.data)}</span>
                              </div>
                            </div>
                            {!alerta.resolvido ? (
                              <Button size="sm" variant="outline" className="text-red-700 border-red-200 hover:bg-red-50 flex-shrink-0"
                                onClick={() => resolverAlertaContagem(alerta.id)}
                                data-testid={`resolver-contagem-${alerta.id}`}
                              >
                                <CheckCircle2 className="h-4 w-4 mr-1" /> Resolver
                              </Button>
                            ) : (
                              <span className="text-xs text-green-600 flex items-center gap-1 flex-shrink-0">
                                <CheckCircle2 className="h-3.5 w-3.5" /> OK
                              </span>
                            )}
                          </div>
                        </div>
                      </Card>
                    );
                  })
                )}
              </div>
            )}
          </>
        )}

        {/* Bloco de Timeout removido — mantém-se apenas a variável maxSessionHoras para uso interno */}
      </div>

      {/* Tab: Meus Alertas */}
      {activeTab === 'meus' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-stone-500">Alertas manuais criados pelo gestor para enviar aos operadores</p>
            <Button size="sm" onClick={() => { setShowFormAlerta(true); setEditandoAlerta(null); const hoje = new Date().toISOString().split('T')[0]; setNovoAlerta({ titulo: '', mensagem: '', data_notificacao: `${hoje}T09:00`, destinatarios: 'todos', prioridade: 'normal' }); }} data-testid="btn-novo-alerta">
              <Plus className="h-4 w-4 mr-1" /> Novo Alerta
            </Button>
          </div>

          {/* Modal de formulário */}
          <Dialog open={showFormAlerta} onOpenChange={setShowFormAlerta}>
            <DialogContent className="sm:max-w-[520px]">
              <DialogHeader>
                <DialogTitle>{editandoAlerta ? 'Editar Alerta' : 'Novo Alerta'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Título *</label>
                  <Input value={novoAlerta.titulo} onChange={(e) => setNovoAlerta({...novoAlerta, titulo: e.target.value})} placeholder="Título do alerta" data-testid="input-alerta-titulo" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Mensagem *</label>
                  <textarea className="w-full border rounded-lg p-2 min-h-[80px] text-sm" value={novoAlerta.mensagem} onChange={(e) => setNovoAlerta({...novoAlerta, mensagem: e.target.value})} placeholder="Mensagem do alerta" data-testid="input-alerta-mensagem" />
                </div>
                <div className="grid grid-cols-4 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">Data</label>
                    <Input 
                      type="date" 
                      value={novoAlerta.data_notificacao ? novoAlerta.data_notificacao.split('T')[0] : ''} 
                      onChange={(e) => {
                        const date = e.target.value;
                        const time = novoAlerta.data_notificacao ? novoAlerta.data_notificacao.split('T')[1] || '09:00' : '09:00';
                        setNovoAlerta({...novoAlerta, data_notificacao: date ? `${date}T${time}` : ''});
                      }} 
                      data-testid="input-alerta-data"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">Hora</label>
                    <Input 
                      type="time" 
                      value={novoAlerta.data_notificacao ? (novoAlerta.data_notificacao.split('T')[1] || '09:00').substring(0, 5) : ''} 
                      onChange={(e) => {
                        const time = e.target.value;
                        const date = novoAlerta.data_notificacao ? novoAlerta.data_notificacao.split('T')[0] : new Date().toISOString().split('T')[0];
                        setNovoAlerta({...novoAlerta, data_notificacao: `${date}T${time}`});
                      }}
                      data-testid="input-alerta-hora"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">Destinatários</label>
                    <select className="w-full border rounded-lg p-2 text-sm" value={novoAlerta.destinatarios} onChange={(e) => setNovoAlerta({...novoAlerta, destinatarios: e.target.value})} data-testid="select-destinatarios">
                      <option value="todos">Todos</option>
                      <option value="gestores">Gestores</option>
                      <option value="operadores">Todos os Operadores</option>
                      {operadoresList.map(op => (
                        <option key={op.id} value={op.id}>{op.nome_completo || op.nome || op.username}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">Prioridade</label>
                    <select className="w-full border rounded-lg p-2 text-sm" value={novoAlerta.prioridade} onChange={(e) => setNovoAlerta({...novoAlerta, prioridade: e.target.value})}>
                      <option value="baixa">Baixa</option>
                      <option value="normal">Normal</option>
                      <option value="alta">Alta</option>
                    </select>
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" size="sm" onClick={() => setShowFormAlerta(false)}>Cancelar</Button>
                  <Button size="sm" onClick={salvarMeuAlerta} disabled={!novoAlerta.titulo || !novoAlerta.mensagem || saving}>
                    {saving ? 'A guardar...' : 'Guardar'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {meusAlertas.length === 0 ? (
            <Card className="p-8 text-center">
              <Bell className="h-12 w-12 text-stone-300 mx-auto mb-3" />
              <p className="text-stone-500 font-medium">Nenhum alerta criado</p>
              <p className="text-sm text-stone-400 mt-1">Crie alertas para enviar aos operadores</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {meusAlertas.map((alerta) => {
                const isAgendado = alerta.estado === 'agendado' || (alerta.data_notificacao && new Date(alerta.data_notificacao) > new Date());
                const estadoBadge = isAgendado ? 'agendado' : 'enviado';
                return (
                <Card key={alerta.id} className={`p-4 ${alerta.prioridade === 'alta' ? 'border-l-4 border-l-red-500' : alerta.prioridade === 'normal' ? 'border-l-4 border-l-amber-500' : ''}`} data-testid={`meu-alerta-${alerta.id}`}>
                  <div className="flex justify-between items-start">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold flex items-center gap-2 text-stone-800">
                        <Bell className="h-4 w-4 flex-shrink-0" />
                        {alerta.titulo}
                        <span className={`text-xs px-2 py-0.5 rounded-full ${alerta.prioridade === 'alta' ? 'bg-red-100 text-red-600' : alerta.prioridade === 'normal' ? 'bg-amber-100 text-amber-600' : 'bg-stone-100 text-stone-600'}`}>
                          {alerta.prioridade}
                        </span>
                        {/* Badge estado Agendado/Enviado */}
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold flex items-center gap-1 ${
                          estadoBadge === 'agendado' 
                            ? 'bg-blue-100 text-blue-700' 
                            : 'bg-emerald-100 text-emerald-700'
                        }`} data-testid={`badge-estado-${alerta.id}`}>
                          {estadoBadge === 'agendado' ? <CalendarClock className="h-3 w-3" /> : <Send className="h-3 w-3" />}
                          {estadoBadge === 'agendado' ? 'Agendado' : 'Enviado'}
                        </span>
                      </h4>
                      <p className="text-sm text-stone-600 mt-1">{alerta.mensagem}</p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-stone-400">
                        <span>Dest: {alerta.destinatarios || 'todos'}</span>
                        {alerta.data_notificacao && <span>Data: {formatDateTime(alerta.data_notificacao)}</span>}
                        {alerta.criado_por && <span className="flex items-center gap-1"><User className="h-3 w-3" /> {alerta.criado_por}</span>}
                      </div>
                    </div>
                    <div className="flex gap-1 flex-shrink-0 ml-3">
                      {/* Copiar */}
                      <button onClick={() => { setEditandoAlerta(null); setNovoAlerta({ titulo: `${alerta.titulo} (cópia)`, mensagem: alerta.mensagem, data_notificacao: '', destinatarios: alerta.destinatarios || 'todos', prioridade: alerta.prioridade || 'normal' }); setShowFormAlerta(true); }} className="p-2 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-700" title="Copiar" data-testid={`btn-copiar-alerta-${alerta.id}`}>
                        <Copy className="h-4 w-4" />
                      </button>
                      {/* Editar */}
                      <button onClick={() => { setEditandoAlerta(alerta); setNovoAlerta({ titulo: alerta.titulo, mensagem: alerta.mensagem, data_notificacao: alerta.data_notificacao || '', destinatarios: alerta.destinatarios || 'todos', prioridade: alerta.prioridade || 'normal' }); setShowFormAlerta(true); }} className="p-2 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-700" title="Editar" data-testid={`btn-editar-alerta-${alerta.id}`}>
                        <Pencil className="h-4 w-4" />
                      </button>
                      {/* Eliminar */}
                      <button onClick={() => eliminarMeuAlerta(alerta.id)} className="p-2 rounded-lg hover:bg-red-50 text-stone-400 hover:text-red-600" title="Eliminar" data-testid={`btn-eliminar-alerta-${alerta.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </Card>
                );
              })}
            </div>
          )}
        </div>
      )}
    </GestorLayout>

    {/* Dialog de Preferências de Alertas */}
    <Dialog open={showPrefsDialog} onOpenChange={setShowPrefsDialog}>
      <DialogContent className="max-w-md rounded-3xl border-0 shadow-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Settings className="h-5 w-5 text-stone-600" />
            Preferencias de Notificacoes
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-stone-500 -mt-2">Ative ou desative os tipos de alertas que deseja receber.</p>
        <div className="space-y-3 mt-2">
          {(prefsAlertas.tipos_disponiveis || []).map((tipo) => {
            const ativo = tiposAtivos.includes(tipo.id);
            const Icon = ICON_MAP[tipo.id] || Bell;
            const cor = COLOR_MAP[tipo.id] || '#64748B';
            return (
              <div
                key={tipo.id}
                className="flex items-center justify-between p-3 rounded-xl transition-all"
                style={{
                  background: ativo ? '#F0FDF4' : '#F8FAFC',
                  border: ativo ? '1.5px solid #BBF7D0' : '1.5px solid #E2E8F0',
                }}
                data-testid={`pref-alerta-${tipo.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: `${cor}15` }}>
                    <Icon className="h-5 w-5" style={{ color: cor }} />
                  </div>
                  <div>
                    <p className="font-bold text-sm text-stone-800">{tipo.label}</p>
                    <p className="text-xs text-stone-500">{tipo.descricao}</p>
                  </div>
                </div>
                <button
                  onClick={() => toggleTipoAlerta(tipo.id)}
                  className="relative w-12 h-7 rounded-full transition-colors"
                  style={{ background: ativo ? '#16A34A' : '#D1D5DB' }}
                  data-testid={`toggle-alerta-${tipo.id}`}
                >
                  <span
                    className="absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform"
                    style={{ left: ativo ? '22px' : '2px' }}
                  />
                </button>
              </div>
            );
          })}
        </div>
        <div className="mt-4">
          <Button onClick={() => setShowPrefsDialog(false)} className="w-full">
            Fechar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}
